import { useCallback, useState } from 'react';
import { GeolocationCoords } from 'app/types/common';
import { useGeolocation } from 'shared/utils/hooks/useGeolocation';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { showNotification } from 'app/providers/NotificationsProvider';
import { useAppDispatch } from 'app/config/storeConfig/hooks';
import { globalConfirmModalActions } from 'app/globalConfirmModal';
import { YandexRouteResult } from '../../model/types';

interface UseYandexMapRouteReturnType {
  routeResult: Nullable<YandexRouteResult>;
  calculateRoute: (destination: GeolocationCoords) => Promise<YandexRouteResult | undefined>;
  resetRoute: () => void;
}

export const useYandexMapRoute = (): UseYandexMapRouteReturnType => {
  const coords = useGeolocation();
  const { t } = useAppTranslation('common');
  const dispatch = useAppDispatch();
  const [routeResult, setRouteResult] = useState<Nullable<YandexRouteResult>>(null);

  const resetRoute = useCallback((): void => {
    setRouteResult(null);
  }, []);

  const closeConfirmModal = useCallback((): void => {
    dispatch(globalConfirmModalActions.setGlobalConfirmModalData(null));
  }, [dispatch]);

  const calculateRoute = useCallback(
    async (destination: GeolocationCoords): Promise<YandexRouteResult | undefined> => {
      resetRoute();

      try {
        const origin = coords;

        if (origin && window.ymaps) {
          const { ymaps } = window;

          // Create routing service
          const multiRoute = new (ymaps as any).multiRouter.MultiRoute({
            referencePoints: [
              [origin.lat, origin.lng],
              [destination.lat, destination.lng],
            ],
            params: {
              routingMode: 'auto',
            },
          });

          // Wait for route calculation
          const result = await new Promise<YandexRouteResult>((resolve, reject) => {
            multiRoute.model.events.add('requestsuccess', () => {
              const activeRoute = multiRoute.getActiveRoute();
              if (activeRoute) {
                const properties = activeRoute.properties.getAll();
                const routeResult: YandexRouteResult = {
                  route: multiRoute,
                  distance: properties.distance?.text || '',
                  duration: properties.duration?.text || '',
                };
                setRouteResult(routeResult);
                resolve(routeResult);
              } else {
                reject(new Error('No route found'));
              }
            });

            multiRoute.model.events.add('requestfail', () => {
              reject(new Error('Route calculation failed'));
            });
          });

          return result;
        } else {
          dispatch(
            globalConfirmModalActions.setGlobalConfirmModalData({
              title: t('Geolocation Permission Denied'),
              description: t(
                'The browser is unable to access your geolocation because permission is not enabled. Please enable location services to allow the website to determine your location.',
              ),
              onOk: closeConfirmModal,
            }),
          );
        }
      } catch (error: CustomAny) {
        console.log(error);
        showNotification('error', t('Error'), t('Error when calculating route'));
      }
    },
    [closeConfirmModal, coords, dispatch, resetRoute, t],
  );

  return { routeResult, calculateRoute, resetRoute };
};
